import { COMPASS_LOOKS, MINIMAP_GLYPHS, MINIMAP_GLYPH_SIZE, MINIMAP_OTHER_LOOKS, PARTY_COLOURS, VISION_CAP_TILES, groundView, isMinimapPlayer, newGroundView, resolveColour, type GroundPicture, type MinimapMark, type MinimapOtherLook, type MinimapOthers, type Size } from "odyssey-shared";

import { paintShape, pipFill } from "./compass-shapes.ts";
/**
 * Paints a minimap field (P1.9-T52; the design's `MapField`, a visual reference). With the map's picture (P1.9-T178,
 * ADR-061's amendment of 2026-10-14) it fills the field with one flat colour for what lies under the picture (off the
 * map, and chunks not composed yet), copies the picture across the whole field, and draws the vision rectangle's 1 px
 * edge and the marks. There is no dark band outside the rectangle (the author's ruling of 2026-10-14): the rectangle
 * says what other players are shown, not what ground is. Without a picture (the gallery, or a page whose map is not
 * booted) it draws flat ground with a tile grid where a tile is two logical pixels or more. Zones are never sent
 * (ADR-054), so nothing here says what zone ground lies in. It draws at one canvas pixel to a logical pixel, so the
 * canvas is scaled by whole device pixels and stays sharp.
 */

/**
 * The ground's colour, the design's.
 */
export const MINIMAP_GROUND = "#3e8c36";

/**
 * The tile grid's colour, the design's.
 */
export const MINIMAP_GRID = "rgba(0,0,0,0.12)";

/**
 * The vision rectangle's edge, the design's.
 */
export const MINIMAP_EDGE = "rgba(201,211,220,0.5)";

/**
 * What lies under the map's picture: off the map, and chunks not composed yet. One flat slate, lighter than the
 * design's old dim and not a green, so it is not read as ground or as a dimmed surround. A draft for the author.
 */
export const MINIMAP_UNDER = "#4a5560";

/**
 * The part of a 2D context the painter uses.
 */
export interface FieldContext {
  fillStyle: string | CanvasGradient | CanvasPattern;
  fillRect(x: number, y: number, width: number, height: number): void;
  drawImage(image: CanvasImageSource, sx: number, sy: number, sw: number, sh: number, dx: number, dy: number, dw: number, dh: number): void;
}

/**
 * The map's picture a field copies from: the canvas, its size and crop (`GroundPicture`), and a version that moves
 * when its pixels may have changed.
 */
export interface FieldGround extends GroundPicture {
  readonly image: CanvasImageSource;
  readonly version: number;
}

const VIEW = newGroundView();

/**
 * The smallest scale of a Nearby field, 6 logical pixels a tile (the HUD's largest is 3): from it the other marks are outlined.
 */
const NEARBY_SCALE_FROM = 6;

/**
 * The fill colour of each kind of other mark, resolved once from the palette.
 */
const OTHER_COLOURS: readonly string[] = MINIMAP_OTHER_LOOKS.map((look: MinimapOtherLook) => resolveColour(look.token));

/**
 * The outline colour of each kind that has its own (a player's white), resolved once; the others hold the empty string.
 */
const OTHER_OUTLINES: readonly string[] = MINIMAP_OTHER_LOOKS.map((look: MinimapOtherLook) => (look.outline === undefined ? "" : resolveColour(look.outline)));

/**
 * The cross colour of each kind that has one (a flagged player's red), resolved once; the others hold the empty string.
 */
const OTHER_CROSSES: readonly string[] = MINIMAP_OTHER_LOOKS.map((look: MinimapOtherLook) => (look.cross === undefined ? "" : resolveColour(look.cross)));

/**
 * The fill colour of each party member's roster place, resolved once from the palette.
 */
const PARTY_FILLS: readonly string[] = PARTY_COLOURS.map((name) => resolveColour(name));

/**
 * The rows of a disc 5 pixels across, as the widths of each row: the player's own mark, whole pixels, round at every
 * zoom.
 */
const OWN_DISC: readonly number[] = [3, 5, 5, 5, 3];

/**
 * The rows of the player's disc with its one pixel outline, 7 pixels across: the disc grown by a pixel on each side,
 * in whole pixels, drawn under it in `ink-900`.
 */
const OWN_OUTLINE: readonly number[] = [3, 5, 7, 7, 7, 5, 3];

/**
 * How far, in pixels, the arms of a flagged player's cross reach from the dot's centre: a 5 by 5 diagonal cross.
 */
const CROSS_REACH = 2;

/**
 * The whole-pixel shift of the ground under a player at a position: what the grid's lines are offset by, so the grid
 * moves with the world and the picture changes only when a line moves a canvas pixel.
 *
 * @param position The player's position across or down, in tiles.
 * @param scale Logical pixels a tile.
 * @returns A whole number of logical pixels, 0 up to one less than the scale.
 */
export function groundPhase(position: number, scale: number): number {
  return Math.round((position - Math.floor(position)) * scale) % scale;
}

/**
 * Paints a field.
 *
 * @param context The canvas's 2D context.
 * @param size The field's size in logical pixels.
 * @param scale Logical pixels a tile.
 * @param x The player's position across, in tiles.
 * @param y The player's position down, in tiles.
 * @param marks The marks to draw, each already inside vision (`minimapMarks`).
 * @param ground The map's picture, or null to draw the flat ground.
 * @param others The other players and creatures to draw as dots (P1.9-T179), each already inside vision, or null for
 *   none. They are drawn after the vision rectangle's edge and before the party's marks and the player's, which is
 *   drawn last, so nothing covers it. Nearby (a scale of `NEARBY_SCALE_FROM` or more) outlines a creature's dot, the HUD
 *   does not; a player's diamond carries its white outline at every zoom (P1.9-T180).
 * @param besides The pictures of the neighbours drawn beside the map (ADR-147 decision 6), each with the player's position
 *   in its own tiles, so its ground lies at the same offset as in the world view; drawn over the slate and under the edge
 *   and the marks.
 * @param besideCount How many of `besides` hold a neighbour.
 */
export function paintField(context: FieldContext, size: Size, scale: number, x: number, y: number, marks: readonly MinimapMark[], ground: FieldGround | null = null, others: MinimapOthers | null = null, besides: readonly { readonly ground: FieldGround | null; readonly x: number; readonly y: number }[] = [], besideCount = besides.length): void {
  const { width, height } = size;
  const centreX = Math.round(width / 2);
  const centreY = Math.round(height / 2);
  context.fillStyle = ground === null ? MINIMAP_GROUND : MINIMAP_UNDER;
  context.fillRect(0, 0, width, height);
  if (ground === null && scale >= 2) {
    context.fillStyle = MINIMAP_GRID;
    const shiftX = groundPhase(x, scale);
    const shiftY = groundPhase(y, scale);
    for (let line = centreX - shiftX; line >= 0; line -= scale) {
      context.fillRect(line, 0, 1, height);
    }
    for (let line = centreX - shiftX + scale; line < width; line += scale) {
      context.fillRect(line, 0, 1, height);
    }
    for (let line = centreY - shiftY; line >= 0; line -= scale) {
      context.fillRect(0, line, width, 1);
    }
    for (let line = centreY - shiftY + scale; line < height; line += scale) {
      context.fillRect(0, line, width, 1);
    }
  }
  const left = Math.max(0, Math.round(centreX - (VISION_CAP_TILES.width / 2) * scale));
  const right = Math.min(width, Math.round(centreX + (VISION_CAP_TILES.width / 2) * scale));
  const top = Math.max(0, Math.round(centreY - (VISION_CAP_TILES.height / 2) * scale));
  const bottom = Math.min(height, Math.round(centreY + (VISION_CAP_TILES.height / 2) * scale));
  if (ground !== null && groundView(VIEW, size, scale, x, y, ground)) {
    context.drawImage(ground.image, VIEW.sx, VIEW.sy, VIEW.width, VIEW.height, VIEW.dx, VIEW.dy, VIEW.width, VIEW.height);
  }
  for (let i = 0; i < besideCount; i += 1) {
    const beside = besides[i];
    const picture = beside?.ground ?? null;
    if (beside !== undefined && picture !== null && groundView(VIEW, size, scale, beside.x, beside.y, picture)) {
      context.drawImage(picture.image, VIEW.sx, VIEW.sy, VIEW.width, VIEW.height, VIEW.dx, VIEW.dy, VIEW.width, VIEW.height);
    }
  }
  context.fillStyle = MINIMAP_EDGE;
  if (top > 0) {
    context.fillRect(left, top, right - left, 1);
  }
  if (bottom < height) {
    context.fillRect(left, bottom - 1, right - left, 1);
  }
  if (left > 0) {
    context.fillRect(left, top, 1, bottom - top);
  }
  if (right < width) {
    context.fillRect(right - 1, top, 1, bottom - top);
  }
  const outline = resolveColour("ink-900");
  if (others !== null) {
    paintOthers(context, others, size, scale, x, y, outline);
  }
  for (let i = 0; i < marks.length; i += 1) {
    const mark = marks[i] as MinimapMark;
    if (mark.kind === "pin") {
      paintShape(context, COMPASS_LOOKS.pin.shape, mark.left, mark.top, pipFill("pin", 0), outline);
    } else if (mark.kind === "place") {
      paintShape(context, COMPASS_LOOKS.place.shape, mark.left, mark.top, pipFill("place", 0), outline);
    } else if (mark.kind !== "self") {
      context.fillStyle = outline;
      context.fillRect(mark.left - 1, mark.top - 1, mark.size + 2, mark.size + 2);
      context.fillStyle = PARTY_FILLS[mark.place] as string;
      context.fillRect(mark.left, mark.top, mark.size, mark.size);
    }
  }
  for (let i = 0; i < marks.length; i += 1) {
    const mark = marks[i] as MinimapMark;
    if (mark.kind === "self") {
      paintDisc(context, mark.left + 1, mark.top + 1, resolveColour("focus-res"), outline);
    }
  }
}

/**
 * Paints the other marks: each whose centre falls inside the field, as its kind's table entry says (`MINIMAP_OTHER_LOOKS`):
 * a 3 px round dot, or a 7 px star or skull, in the kind's token, with a one pixel outline on a dot in Nearby. Other
 * players are drawn after everything else, so where they meet one they are on top.
 *
 * @param context The canvas's 2D context.
 * @param others The buffer.
 * @param size The field's size.
 * @param scale Logical pixels a tile.
 * @param x The player's position across, in tiles.
 * @param y The player's position down.
 * @param outline The outline colour.
 */
function paintOthers(context: FieldContext, others: MinimapOthers, size: Size, scale: number, x: number, y: number, outline: string): void {
  const centreX = Math.round(size.width / 2);
  const centreY = Math.round(size.height / 2);
  const outlined = scale >= NEARBY_SCALE_FROM;
  for (let pass = 0; pass < 2; pass += 1) {
    for (let i = 0; i < others.count; i += 1) {
      const kind = others.kind[i] as number;
      if ((isMinimapPlayer(kind) ? 1 : 0) !== pass) {
        continue;
      }
      const px = Math.round(centreX + ((others.x[i] as number) - x) * scale);
      const py = Math.round(centreY + ((others.y[i] as number) - y) * scale);
      if (px < 0 || py < 0 || px >= size.width || py >= size.height) {
        continue;
      }
      const look = MINIMAP_OTHER_LOOKS[kind] as MinimapOtherLook;
      if (look.shape === "dot") {
        if (look.outline !== undefined) {
          paintDot(context, px, py, 2, OTHER_OUTLINES[kind] as string);
        } else if (outlined) {
          paintDot(context, px, py, 2, outline);
        }
        paintDot(context, px, py, 1, OTHER_COLOURS[kind] as string);
        if (look.cross !== undefined) {
          paintCross(context, px, py, OTHER_CROSSES[kind] as string);
        }
      } else {
        paintGlyph(context, look.shape, px, py, OTHER_COLOURS[kind] as string);
      }
    }
  }
}

/**
 * Paints a glyph centred on a pixel, each run of set pixels in a row as one fill.
 *
 * @param context The canvas's 2D context.
 * @param shape The glyph.
 * @param x The centre pixel across.
 * @param y The centre pixel down.
 * @param colour The fill colour.
 */
function paintGlyph(context: FieldContext, shape: "star" | "skull", x: number, y: number, colour: string): void {
  const rows = MINIMAP_GLYPHS[shape];
  const half = (MINIMAP_GLYPH_SIZE - 1) / 2;
  context.fillStyle = colour;
  for (let row = 0; row < MINIMAP_GLYPH_SIZE; row += 1) {
    const line = rows[row] as string;
    let column = 0;
    while (column < MINIMAP_GLYPH_SIZE) {
      if (line[column] !== "#") {
        column += 1;
        continue;
      }
      let end = column;
      while (end < MINIMAP_GLYPH_SIZE && line[end] === "#") {
        end += 1;
      }
      context.fillRect(x - half + column, y - half + row, end - column, 1);
      column = end;
    }
  }
}

/**
 * Paints the player's own mark: a filled disc 5 pixels across in whole pixels, rows of 3, 5, 5, 5 and 3, centred on a
 * pixel, inside a one pixel outline that makes it 7 across and keeps it in view over water (P1.9-T180).
 *
 * @param context The canvas's 2D context.
 * @param x The centre pixel across.
 * @param y The centre pixel down.
 * @param colour The fill colour.
 * @param outline The outline colour.
 */
function paintDisc(context: FieldContext, x: number, y: number, colour: string, outline: string): void {
  context.fillStyle = outline;
  for (let row = 0; row < OWN_OUTLINE.length; row += 1) {
    const width = OWN_OUTLINE[row] as number;
    context.fillRect(x - (width - 1) / 2, y + row - 3, width, 1);
  }
  context.fillStyle = colour;
  for (let row = 0; row < OWN_DISC.length; row += 1) {
    const width = OWN_DISC[row] as number;
    context.fillRect(x - (width - 1) / 2, y + row - 2, width, 1);
  }
}

/**
 * Paints a cross over a mark: the centre pixel and the four diagonals out to `CROSS_REACH`, whole pixels.
 *
 * @param context The canvas's 2D context.
 * @param x The centre pixel across.
 * @param y The centre pixel down.
 * @param colour The cross's colour.
 */
function paintCross(context: FieldContext, x: number, y: number, colour: string): void {
  context.fillStyle = colour;
  context.fillRect(x, y, 1, 1);
  for (let reach = 1; reach <= CROSS_REACH; reach += 1) {
    context.fillRect(x - reach, y - reach, 1, 1);
    context.fillRect(x + reach, y - reach, 1, 1);
    context.fillRect(x - reach, y + reach, 1, 1);
    context.fillRect(x + reach, y + reach, 1, 1);
  }
}

/**
 * Paints another mark's dot: whole pixels in rows, a diamond of a radius about a pixel, so a radius of 1 is a 3 by 3
 * with its corners left out (1 by 3, 3 by 1 on the middle row, 1 by 3) and a radius of 2 is the outline one pixel
 * larger.
 *
 * @param context The canvas's 2D context.
 * @param x The centre pixel across.
 * @param y The centre pixel down.
 * @param radius The diamond's radius in pixels.
 * @param colour The fill colour.
 */
function paintDot(context: FieldContext, x: number, y: number, radius: number, colour: string): void {
  context.fillStyle = colour;
  for (let row = -radius; row <= radius; row += 1) {
    const half = radius - Math.abs(row);
    context.fillRect(x - half, y + row, 2 * half + 1, 1);
  }
}

/**
 * One filled rectangle of a legend swatch, in the swatch's own pixels.
 */
export interface SwatchRect {
  readonly fill: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * A legend swatch: the pixels a mark is painted in, drawn by the painter's own routines so the legend shows exactly the
 * mark the field draws.
 */
export interface Swatch {
  readonly width: number;
  readonly height: number;
  readonly rects: readonly SwatchRect[];
}

/**
 * The size of a mark's legend swatch, the outlined disc's: 7 by 7 pixels, with the mark centred.
 */
export const SWATCH_SIZE = 7;

const SWATCHES = new Map<string, Swatch>();

/**
 * The swatch of a mark for the Nearby legend (P1.9-T180): the player's outlined disc, the party's five colours side by
 * side, or the dot of a kind of other mark as Nearby draws it (outlined, a player's with its white outline and a
 * flagged player's with its cross). The result is kept, so it is built once.
 *
 * @param mark `"self"`, `"party"`, or the kind of an other mark whose shape is a dot.
 * @returns The swatch.
 * @throws RangeError when the kind is unknown or its shape is not a dot.
 */
export function swatchOf(mark: "self" | "party" | number): Swatch {
  const key = String(mark);
  const kept = SWATCHES.get(key);
  if (kept !== undefined) {
    return kept;
  }
  const rects: SwatchRect[] = [];
  const context: FieldContext = {
    fillStyle: "",
    fillRect(x: number, y: number, w: number, h: number): void {
      rects.push({ fill: String(context.fillStyle), x, y, w, h });
    },
    drawImage(): void {},
  };
  const outline = resolveColour("ink-900");
  const centre = (SWATCH_SIZE - 1) / 2;
  let width: number = SWATCH_SIZE;
  let height: number = SWATCH_SIZE;
  if (mark === "self") {
    paintDisc(context, centre, centre, resolveColour("focus-res"), outline);
  } else if (mark === "party") {
    width = PARTY_FILLS.length * 4 - 1;
    height = 3;
    for (let place = 0; place < PARTY_FILLS.length; place += 1) {
      context.fillStyle = PARTY_FILLS[place] as string;
      context.fillRect(place * 4, 0, 3, 3);
    }
  } else {
    const look = MINIMAP_OTHER_LOOKS[mark];
    if (look === undefined || look.shape !== "dot") {
      throw new RangeError(`kind ${mark} has no dot to draw as a swatch`);
    }
    paintDot(context, centre, centre, 2, look.outline === undefined ? outline : (OTHER_OUTLINES[mark] as string));
    paintDot(context, centre, centre, 1, OTHER_COLOURS[mark] as string);
    if (look.cross !== undefined) {
      paintCross(context, centre, centre, OTHER_CROSSES[mark] as string);
    }
  }
  const swatch: Swatch = { width, height, rects };
  SWATCHES.set(key, swatch);
  return swatch;
}
